import React, {useEffect, useState} from "react";
import {Animated, FlatList, PanResponder, StyleSheet, Text, TouchableOpacity, TouchableWithoutFeedback, useWindowDimensions, View} from "react-native";
import {Box, HStack, VStack} from "native-base";
import {useRecoilState, useRecoilValue} from "recoil";
import {cartState} from "./state/cartState";
import {orderState} from "./state/orderState";
import {Cart, CartItem, mapToItems, OptionItemData, OptionListData} from "./lib/model";
import {useNavigation} from "@react-navigation/native";
import DashedLine from "react-native-dashed-line";
import apps from "./lib/apps";
import {PALETTE} from "./lib/palette";
import {money} from "./lib/utils";
import ThumbImage from "./component/ThumbImage";
import {useScrollMore, ScrollMoreHint} from "./component/ScrollMore";

const CartTitle: React.FC<any> = () => {
    const cart = useRecoilValue(cartState);
    const yourOrder = useRecoilValue(orderState);

    return (
        <HStack style={styles.cartLableBox}>
            <Text style={styles.cartLabel}>{cart.shop.name}</Text>
            <Text style={styles.cartTable}>{yourOrder.getOrderInfo()}</Text>
        </HStack>
    );
};

const CartItems: React.FC<{ onCartItemSelected: (selectedTag: string) => void }> = ({onCartItemSelected}) => {
    const [cart, setCart] = useRecoilState(cartState);

    const [items, setItems] = useState(mapToItems([]));
    const {showHint, scrollToEnd, scrollProps} = useScrollMore();   // 스크롤 "더 있음" 힌트

    useEffect(() => {
        setItems(mapToItems(cart.items));
    }, [cart]);

    const deleteItem = (menuTag: string) => {
        setCart(cart.removeItem(menuTag));
    };

    return (
        <Box style={{flex: 1}}>
            <FlatList
                {...scrollProps}
                data={items}
                keyExtractor={(item) => item.key}
                style={{overscrollBehavior: "none"} as any}
                // 항목 사이 점선(기준: Ordered/Order Details와 동일). 마지막 항목 뒤에는 안 들어감.
                ItemSeparatorComponent={() => <DashedLine dashLength={3} dashGap={3} dashThickness={1} dashColor={PALETTE.DIVIDER} style={{height: 1}} />}
                renderItem={({item}) => (
                    <HStack style={styles.itemList}>
                        <TouchableWithoutFeedback onPress={() => onCartItemSelected(item.key)}>
                            <HStack style={styles.itemInner}>
                                <ThumbImage shopid={apps.currentShop?.shopid ?? ""} menuid={item.menuid} imageVer={item.imageVer} style={styles.itemThumb} />
                                <VStack style={styles.itemLeft}>
                                    <Text style={styles.itemName}>{item.name}</Text>
                                    <Text style={styles.itemOptions}>{item.optionsDescr()}</Text>
                                    {item.note ? <Text style={styles.itemNote}>📝 {item.note}</Text> : null}
                                </VStack>
                                <VStack style={styles.itemRight}>
                                    <Text style={styles.itemPrice}>{money(item.total)}</Text>
                                    <View style={styles.itemQtyChip}><Text style={styles.itemQtyChipText}>×{item.qty}</Text></View>
                                </VStack>
                            </HStack>
                        </TouchableWithoutFeedback>
                        <TouchableOpacity onPress={() => deleteItem(item.key)} style={styles.itemDeleteIcon}>
                            <Text style={styles.itemDeleteIconText}>🗑</Text>
                        </TouchableOpacity>
                    </HStack>
                )}
            />
            <ScrollMoreHint visible={showHint} onPress={scrollToEnd}/>
        </Box>
    );
}

const CartButton: React.FC<{ onAdd: () => void }> = ({onAdd}) => {
    const cart = useRecoilValue(cartState);
    const accent = apps.getAccent();
    const onAccent = apps.onAccent();

    // 카트에 메뉴 아이템이 하나도 없으면(모두 삭제) 체크아웃 차단. 금액($0)이 아닌 아이템 수로 판정 —
    // 무료(0원) 아이템이 담긴 주문은 통과시켜야 하므로 cart.total()이 아니라 items.length로 게이트한다.
    const isEmpty = cart.items.length === 0;

    return (
        <Box style={styles.cartFoot}>
            <HStack style={styles.subTotalPane}>
                <Text style={styles.subTotalLabel}>Subtotal</Text>
                <Text style={styles.subTotal}>{money(cart.total())}</Text>
            </HStack>
            <TouchableOpacity onPress={onAdd} disabled={isEmpty}
                              style={[styles.checkoutBtn, {backgroundColor: accent}, isEmpty && styles.checkoutBtnDisabled]}>
                <Text style={[styles.checkoutBtnText, {color: onAccent}]}>
                    {isEmpty ? "Your cart is empty" : `Go To Checkout · ${money(cart.total())}`}
                </Text>
            </TouchableOpacity>
        </Box>
    );
};

function onCartItemSelected(selectedTag: string, navigation) {
    navigation.replace("Menu", {selectedTag: selectedTag});
}

// 시트 높이 소스 = '현재' 레이아웃 뷰포트 높이. 웹에서 소프트 키보드는 visual viewport만 줄이고
// layout viewport(documentElement.clientHeight)는 그대로다 → 키보드엔 면역이면서 실제 창 리사이즈
// (태블릿/데스크탑/회전)에는 양방향으로 반응한다. useWindowDimensions는 리사이즈 시 재렌더 트리거용.
// (예전 observedMaxHeight 모듈 전역은 '역대 최대'만 단조 기억→축소를 무시→과거에 창이 컸으면 현재
//  뷰포트 대비 시트가 과대해져 95%/80% 들쭉날쭉·태블릿/윈도우서 시트가 화면을 넘던 버그의 원인이었다.)
function useViewportHeight(): number {
    const {height} = useWindowDimensions();   // 창 크기 변화 시 재렌더 트리거
    if (typeof document !== "undefined" && document.documentElement?.clientHeight) {
        return document.documentElement.clientHeight;   // 레이아웃 뷰포트(키보드 면역·리사이즈 반응)
    }
    return height;
}

function CartView({navigation}) {
    const cart = useRecoilValue(cartState);
    const screenHeight = useViewportHeight();   // 현재 레이아웃 뷰포트(키보드 면역·리사이즈 반응)
    const {width, height} = useWindowDimensions();   // 기기 분류용(짧은 변 = sw600dp)

    // 시트 높이 스냅포인트. 기본 50% / 확대 85% 이되, 두 값 모두 (1) 절대 px 상한(MAX_SHEET_PX)과
    // (2) 현재 뷰포트 - 상단 여백 으로 clamp한다 → 큰 화면(태블릿/데스크탑)서 과대해지지 않고, 작은 화면서도
    // 뷰포트를 넘지 않는다(핸들이 화면 위로 잘리거나 체크아웃 버튼이 아래로 밀려나지 않음).
    // 기기 분류는 '가장 짧은 변'(Android sw600dp 방식)으로 — 회전·폴더블과 무관하다. 폭만 보면
    // Flip 7(360×840) 같은 세로로 긴 폰이 가로회전 시 width 840→큰 화면으로 오분류되어 상한이 잘못 커진다.
    // min(width,height): Flip 7=360(폰), iPad 세로/가로=768(태블릿) → 어느 방향이든 일관.
    const isLargeScreen = Math.min(width, height) >= 600;
    // 확대 시트 최대 높이. 폰은 640 유지(기존과 동일 — 영향 없음), 태블릿/데스크탑은 상한을 올려 큰 화면서도
    // 위로 드래그하면 확대되게 한다. 어느 경우든 아래 0.85·vh·(vh-여백) clamp가 과대·화면초과를 막는다
    // (기본 50% 스냅은 상한과 무관하게 항상 0.5·vh).
    const MAX_SHEET_PX = isLargeScreen ? 900 : 640;
    const TOP_GAP = 24;         // 최상단은 항상 비워 둔다
    const HEIGHT_EXPANDED = Math.min(MAX_SHEET_PX, screenHeight - TOP_GAP, Math.round(screenHeight * 0.85));
    const HEIGHT_DEFAULT = Math.min(HEIGHT_EXPANDED, Math.round(screenHeight * 0.5));
    const OFFSCREEN = screenHeight + 100;   // 어떤 화면 크기에서도 완전히 가려지는 값
    const DISMISS = 30;         // 50%보다 이 값(px)만 더 내려도 바로 닫힘(중간 재정지 없이 사라짐)

    // B5: 핸들을 아래로 드래그하면 시트가 따라 내려가고, threshold(DISMISS) 초과 시 닫힘(goBack),
    // 아니면 원위치로 스프링백. 위로 드래그하면 시트 높이가 확대(HEIGHT_EXPANDED)까지 커진다.
    // PanResponder는 핸들에만 부착 → 리스트 스크롤과 충돌 없음. 웹이라 useNativeDriver:false.
    // 진입 시 시트가 화면 아래(OFFSCREEN)에서 제자리(0)로 스프링업.
    const translateY = React.useRef(new Animated.Value(OFFSCREEN)).current;
    // 시트 높이(px). 기본 50%에서 시작, 위로 드래그하면 HEIGHT_EXPANDED까지 늘어난다.
    const sheetHeight = React.useRef(new Animated.Value(HEIGHT_DEFAULT)).current;
    // 드래그 제스처 시작 시점의(스냅된) 높이 — release 시 "원위치"의 기준.
    const baseHeightRef = React.useRef(HEIGHT_DEFAULT);
    const dragStartHeightRef = React.useRef(HEIGHT_DEFAULT);

    // 스냅 상수를 ref로 최신 유지. PanResponder는 1회만 생성되어 첫 렌더의 상수를 클로저로 캡처하므로,
    // 뷰포트가 바뀌어 스냅 값이 달라졌을 때 스테일 값을 쓰지 않도록 항상 snap.current로 읽는다.
    const snap = React.useRef({HEIGHT_DEFAULT, HEIGHT_EXPANDED, OFFSCREEN, DISMISS});
    snap.current = {HEIGHT_DEFAULT, HEIGHT_EXPANDED, OFFSCREEN, DISMISS};

    // 진입 애니메이션: 시트가 화면 아래에서 제자리로 올라온다.
    React.useEffect(() => {
        Animated.spring(translateY, {toValue: 0, bounciness: 4, speed: 14, useNativeDriver: false}).start();
    }, []);

    // 뷰포트가 실제로 바뀌면(태블릿/데스크탑 리사이즈·회전) 현재 스냅 상태(기본/확대)에 맞춰 시트 높이를 다시
    // 맞춘다. 예전엔 sheetHeight/baseHeight가 첫 렌더값에 고정돼 리사이즈 후 시트가 안 따라오거나 뷰포트를 넘었다.
    React.useEffect(() => {
        const atExpanded = baseHeightRef.current > (HEIGHT_DEFAULT + HEIGHT_EXPANDED) / 2;
        const target = atExpanded ? HEIGHT_EXPANDED : HEIGHT_DEFAULT;
        baseHeightRef.current = target;
        Animated.spring(sheetHeight, {toValue: target, bounciness: 4, useNativeDriver: false}).start();
    }, [HEIGHT_DEFAULT, HEIGHT_EXPANDED]);

    const panResponder = React.useRef(
        PanResponder.create({
            onStartShouldSetPanResponder: () => false,
            // 세로 드래그면 위·아래 모두 캡처(위로도 흡수) → 위 드래그가 브라우저로 새어 body 스크롤/툴바
            // 숨김·흰 여백을 유발하지 않게 한다.
            onMoveShouldSetPanResponder: (_e, g) => Math.abs(g.dy) > 4 && Math.abs(g.dy) > Math.abs(g.dx),
            onPanResponderGrant: () => {
                dragStartHeightRef.current = baseHeightRef.current;
            },
            onPanResponderMove: (_e, g) => {
                // 위·아래 모두 높이 기반: 핸들이 손가락을 따라온다. 아래로 내리면 시트가 줄고, 위로 올리면 커진다.
                const newHeight = Math.min(snap.current.HEIGHT_EXPANDED, dragStartHeightRef.current - g.dy);
                sheetHeight.setValue(Math.max(0, newHeight));
                translateY.setValue(0);
            },
            onPanResponderRelease: (_e, g) => {
                // 놓은 지점 높이로 스냅: 확대 / 기본 / 닫힘. 50%보다 DISMISS만큼 더 내려야 닫히므로,
                // 아래로 내리다 중간에서 놓으면 기본(50%)에 한 번 걸린다. 스냅 값은 snap.current(최신)로 읽는다.
                const {HEIGHT_DEFAULT, HEIGHT_EXPANDED, OFFSCREEN, DISMISS} = snap.current;
                const finalHeight = dragStartHeightRef.current - g.dy;
                const MID = (HEIGHT_EXPANDED + HEIGHT_DEFAULT) / 2;   // 확대↔기본 중간
                const DISMISS_BELOW = HEIGHT_DEFAULT - DISMISS;       // 기본보다 DISMISS(px) 더 내리면 닫힘
                if (finalHeight >= MID) {
                    baseHeightRef.current = HEIGHT_EXPANDED;
                    Animated.spring(sheetHeight, {toValue: HEIGHT_EXPANDED, bounciness: 4, useNativeDriver: false}).start();
                    Animated.spring(translateY, {toValue: 0, bounciness: 6, useNativeDriver: false}).start();
                } else if (finalHeight >= DISMISS_BELOW) {
                    baseHeightRef.current = HEIGHT_DEFAULT;
                    Animated.spring(sheetHeight, {toValue: HEIGHT_DEFAULT, bounciness: 4, useNativeDriver: false}).start();
                    Animated.spring(translateY, {toValue: 0, bounciness: 6, useNativeDriver: false}).start();
                } else {
                    Animated.timing(translateY, {toValue: OFFSCREEN, duration: 180, useNativeDriver: false})
                        .start(() => navigation.goBack());
                }
            },
            onPanResponderTerminate: () => {
                Animated.spring(sheetHeight, {toValue: dragStartHeightRef.current, useNativeDriver: false}).start();
                Animated.spring(translateY, {toValue: 0, useNativeDriver: false}).start();
            },
        })
    ).current;

    const checkout = () => {
        navigation.replace("Checkout");
    };

    return (
        <VStack flex="1">
            {/* 전체 화면 어두운 배경(시트 뒤까지 깔림) — 핸들 드래그로 시트가 내려가도 흰 여백 대신
                어두운 배경이 보이게. 탭하면 닫힘. 상단 spacer는 pointerEvents=none으로 탭을 backdrop에 넘긴다. */}
            <TouchableWithoutFeedback onPress={() => navigation.goBack()}>
                <View style={styles.backdrop}/>
            </TouchableWithoutFeedback>
            <View style={{flex: 1}} pointerEvents="none"/>
            <Animated.View style={[styles.cart, {height: sheetHeight, transform: [{translateY}]}]}>
                <View {...panResponder.panHandlers} style={styles.handleZone}>
                    <Box style={styles.sheetHandle}/>
                </View>
                <CartTitle/>
                <Text style={styles.cartHint}>Tip: tap 🗑 to remove an item.</Text>
                <CartItems onCartItemSelected={(selectedTag) => {onCartItemSelected(selectedTag, navigation)}}/>
                <CartButton onAdd={() => {
                    checkout();
                }}/>
            </Animated.View>
        </VStack>
    )
}

const styles = StyleSheet.create({
    backdrop: {
        position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: "rgba(20,24,32,0.45)",
    }, cart: {
        width: "100%", backgroundColor: PALETTE.SURFACE,
        borderTopLeftRadius: 24, borderTopRightRadius: 24,
        // height는 CartView가 Animated.Value(sheetHeight)로 동적 부여(기본 50%~확대 80%).
    }, handleZone: {
        width: "100%", alignItems: "center",
        paddingTop: 4, paddingBottom: 6,
        cursor: "grab",   // 웹에서 드래그 가능함을 표시
    }, sheetHandle: {
        width: 40, height: 4, borderRadius: 2,
        backgroundColor: PALETTE.BORDER,
        alignSelf: "center", marginTop: 10, marginBottom: 4,
    }, cartLableBox: {
        width: "100%",
        paddingHorizontal: 18,
        paddingTop: 6,
        paddingBottom: 12,
        justifyContent: "space-between",
        alignItems: "baseline",
        borderBottomWidth: 1,
        borderBottomColor: PALETTE.BORDER,
    }, cartLabel: {
        fontSize: 17,
        fontFamily: "NotoSans-Bold",
        fontWeight: "800",
        letterSpacing: -0.4,
        color: PALETTE.TEXT,
    }, cartTable: {
        fontSize: 13,
        fontFamily: "NotoSans-Regular",
        fontWeight: "600",
        letterSpacing: -0.4,
        color: PALETTE.MUTED,
    }, cartHint: {
        fontSize: 11,
        fontFamily: "NotoSans-Regular",
        color: PALETTE.MUTED,
        paddingHorizontal: 18,
        paddingTop: 8,
    }, itemList: {
        width: "100%", backgroundColor: PALETTE.SURFACE,
        paddingVertical: 12, paddingHorizontal: 18,
        alignItems: "center",
    }, itemInner: {
        flex: 1, alignItems: "center",
    }, itemThumb: {
        width: 44, height: 44, borderRadius: PALETTE.RADIUS_SM, marginRight: 10, backgroundColor: PALETTE.BORDER,
    }, itemLeft: {
        flex: 1, justifyContent: "center",
    }, itemName: {
        fontSize: 14,
        fontFamily: "NotoSans-Bold",
        fontWeight: "700",
        letterSpacing: -0.3,
        color: PALETTE.TEXT,
    }, itemOptions: {
        fontSize: 12,
        fontFamily: "NotoSans-Regular",
        letterSpacing: -0.3,
        color: PALETTE.MUTED,
        marginTop: 2,
    }, itemNote: {
        fontSize: 11,
        fontFamily: "NotoSans-Regular",
        letterSpacing: -0.3,
        color: PALETTE.MUTED,
        fontStyle: "italic",
        marginTop: 2,
    }, itemRight: {
        alignItems: "flex-end", justifyContent: "flex-start",
    }, itemPrice: {
        fontSize: 14,
        fontFamily: "NotoSans-Bold",
        fontWeight: "800",
        color: PALETTE.TEXT,
        letterSpacing: -0.3,
    }, itemQtyChip: {
        marginTop: 4,
        borderRadius: PALETTE.RADIUS_PILL,
        paddingHorizontal: 8,
        paddingVertical: 1,
        backgroundColor: PALETTE.QTY_CHIP,
    }, itemQtyChipText: {
        fontSize: 11,
        fontFamily: "NotoSans-Bold",
        fontWeight: "700",
        color: "#FFFFFF",
        letterSpacing: -0.2,
    }, itemDeleteIcon: {
        width: 30, height: 30,
        borderRadius: PALETTE.RADIUS_SM,
        backgroundColor: PALETTE.CHIP_BG,
        alignItems: "center",
        justifyContent: "center",
        marginLeft: 12,
    }, itemDeleteIconText: {
        fontSize: 15,
        fontFamily: "NotoSans-Regular",
    }, cartFoot: {
        paddingHorizontal: 18,
        paddingVertical: 14,
        borderTopWidth: 1,
        borderTopColor: PALETTE.DIVIDER,
    }, subTotalPane: {
        justifyContent: "space-between",
        marginBottom: 10,
    }, subTotalLabel: {
        fontSize: 14,
        fontFamily: "NotoSans-Regular",
        color: PALETTE.MUTED,
    }, subTotal: {
        fontSize: 14,
        fontFamily: "NotoSans-Bold",
        fontWeight: "800",
        color: PALETTE.TEXT,
    }, checkoutBtn: {
        width: "100%",
        padding: 14,
        borderRadius: PALETTE.RADIUS_MD,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: PALETTE.ACCENT, // inert fallback; overridden at render time by {backgroundColor: accent}
    }, checkoutBtnDisabled: {
        opacity: 0.5,
    }, checkoutBtnText: {
        fontSize: 15,
        fontFamily: "NotoSans-Bold",
        fontWeight: "800",
        color: "white", // inert fallback; overridden at render time by {color: onAccent}
    },
});

export default CartView;
